import React, { useCallback } from 'react'
import { EventContentArg as FullCalendarEventContentArg } from '@fullcalendar/core'
import { ExtendedEvent } from '../types'
import { MonthViewEvent } from './MonthViewEvent'
import { TimeGridEvent } from './TimeGridEvent'
import { DayViewEvent } from './DayViewEvent'
import MobileViewEvent from './MobileViewEvent'
import useDeviceDetect from '../hooks/useDeviceDetect'
import { announceForScreenReader } from '../utils/accessibility'

interface EventRendererProps {
  eventInfo: FullCalendarEventContentArg
  calendarApi: any
}

/**
 * EventRenderer component that renders events based on the view type and screen size
 *
 * This component decides which event component to render based on the current view
 * and screen size, providing optimized rendering for different devices.
 */
const EventRenderer: React.FC<EventRendererProps> = ({ eventInfo, calendarApi }) => {
  const { event, view } = eventInfo
  const { isMobile, isSmallMobile } = useDeviceDetect()

  // Determine the current view type
  const viewType = calendarApi?.view.type || ''
  const isMonthView = viewType === 'dayGridMonth'
  const isDayView = viewType === 'timeGridDay'

  // Convert the event to our ExtendedEvent type
  const extendedEvent = event as unknown as ExtendedEvent

  // Handle event click
  const handleEventClick = useCallback(() => {
    // The actual click is handled by FullCalendar's eventClick handler
    // This is just for keyboard navigation

    // Announce the event for screen readers
    const title = extendedEvent.title || 'Untitled event'
    announceForScreenReader(`Selected event: ${title}`)
  }, [extendedEvent])

  // Handle keyboard navigation between events
  const handleKeyboardNavigation = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right') => {
      if (!calendarApi) return

      // Get all events in the current view
      const allEvents = calendarApi.getEvents()
      if (!allEvents || allEvents.length === 0) return

      // Find the current event
      const currentEventId = extendedEvent.id
      const currentEventIndex = allEvents.findIndex((e: any) => e.id === currentEventId)
      if (currentEventIndex === -1) return

      // Determine the next event based on direction
      let nextEventIndex: number

      if (isMonthView) {
        // In month view, left/right navigates between events on the same day
        // up/down navigates to the same day in the previous/next week
        const currentDate =
          extendedEvent.start instanceof Date
            ? extendedEvent.start
            : typeof extendedEvent.start === 'string'
            ? new Date(extendedEvent.start)
            : null

        if (!currentDate) return

        if (direction === 'left' || direction === 'right') {
          // Find events on the same day
          const sameDay = allEvents.filter((e: any) => {
            const eventDate =
              e.start instanceof Date
                ? e.start
                : typeof e.start === 'string'
                ? new Date(e.start)
                : null

            return (
              eventDate &&
              eventDate.getFullYear() === currentDate.getFullYear() &&
              eventDate.getMonth() === currentDate.getMonth() &&
              eventDate.getDate() === currentDate.getDate()
            )
          })

          const currentIndexInDay = sameDay.findIndex((e: any) => e.id === currentEventId)
          if (currentIndexInDay === -1) return

          if (direction === 'left') {
            nextEventIndex = allEvents.indexOf(sameDay[Math.max(0, currentIndexInDay - 1)])
          } else {
            nextEventIndex = allEvents.indexOf(
              sameDay[Math.min(sameDay.length - 1, currentIndexInDay + 1)]
            )
          }
        } else {
          // Create a new date for the previous/next week
          const newDate = new Date(currentDate)
          if (direction === 'up') {
            newDate.setDate(newDate.getDate() - 7)
          } else {
            newDate.setDate(newDate.getDate() + 7)
          }

          // Find the closest event to the new date
          let closestEvent = null
          let minDiff = Infinity

          for (const e of allEvents) {
            const eventDate =
              e.start instanceof Date
                ? e.start
                : typeof e.start === 'string'
                ? new Date(e.start)
                : null

            if (!eventDate) continue

            if (
              eventDate.getFullYear() === newDate.getFullYear() &&
              eventDate.getMonth() === newDate.getMonth() &&
              eventDate.getDate() === newDate.getDate()
            ) {
              const diff = Math.abs(eventDate.getTime() - newDate.getTime())
              if (diff < minDiff) {
                minDiff = diff
                closestEvent = e
              }
            }
          }

          if (closestEvent) {
            nextEventIndex = allEvents.indexOf(closestEvent)
          } else {
            return // No event found in the target week
          }
        }
      } else {
        // In time grid views, up/down navigates between events in time order
        if (direction === 'up' || direction === 'left') {
          nextEventIndex = Math.max(0, currentEventIndex - 1)
        } else {
          nextEventIndex = Math.min(allEvents.length - 1, currentEventIndex + 1)
        }
      }

      // Focus the next event
      if (
        nextEventIndex !== currentEventIndex &&
        nextEventIndex >= 0 &&
        nextEventIndex < allEvents.length
      ) {
        const nextEvent = allEvents[nextEventIndex]

        // Scroll to the event
        calendarApi.scrollToEvent(nextEvent)

        // Find the event element and focus it
        setTimeout(() => {
          const eventEl = document.querySelector(`[data-event-id="${nextEvent.id}"]`) as HTMLElement
          if (eventEl) {
            eventEl.focus()

            // Announce the event for screen readers
            const title = nextEvent.title || 'Untitled event'
            announceForScreenReader(`Navigated to event: ${title}`)
          }
        }, 100)
      }
    },
    [calendarApi, extendedEvent, isMonthView]
  )

  // For mobile devices, use the mobile-optimized component
  if (isMobile) {
    return (
      <MobileViewEvent
        event={extendedEvent}
        isCompact={isSmallMobile || isMonthView}
        onClick={handleEventClick}
        onKeyboardNavigation={handleKeyboardNavigation}
      />
    )
  }

  // For desktop, render the appropriate component based on the view type
  if (isMonthView) {
    return (
      <MonthViewEvent
        event={extendedEvent}
        onClick={handleEventClick}
        onKeyboardNavigation={handleKeyboardNavigation}
      />
    )
  }

  if (isDayView) {
    return (
      <DayViewEvent
        event={extendedEvent}
        onClick={handleEventClick}
        onKeyboardNavigation={handleKeyboardNavigation}
      />
    )
  }

  // Default to TimeGridEvent for week view and any other views
  return (
    <TimeGridEvent
      event={extendedEvent}
      onClick={handleEventClick}
      onKeyboardNavigation={handleKeyboardNavigation}
    />
  )
}

export default EventRenderer
